import { OTHER_PAYMENT_MAX_AMOUNT, OTHER_PAYMENT_MIN_AMOUNT, PORTMONE_AMOUNT_FIELD_NAME } from 'utils/constants';
import { CHECK_RESPONSE, CURRENCY_LABEL, PROVIDER_TYPE } from 'utils/enums';
import {
	IAddServiceFieldValueRequest,
	IBillCreateRequestArrayDataItem,
	IBillRequestData,
	ICreateBillRequestServiceFieldsData,
	IGetServiceDataResponse,
	INormalizedServiceBillRequestItem,
	IOtherServicePaymentRequest,
	IOtherServiceProviderRequest,
	IOtherServiceProviderServiceFieldsData,
	IReceiptOtherServiceResponse,
	IServiceBillError,
	IServiceBillRequestData,
	IServiceBillRequestGroup,
	IServiceDataObject,
	IServiceFieldResponse,
	IServiceFieldValueResponse,
	IServicePaymentInstructionGroup,
	ITariffItemData,
	IUpdateServiceFieldValueRequest,
	ServiceFeeRecord,
} from 'utils/types';
import { convertUAHToCoins } from './currency';
import { validateAmountLimits } from './validate-amount-limits';

export const getServiceInputConfig = (
	data: IAddServiceFieldValueRequest | IUpdateServiceFieldValueRequest,
	fieldsList: IServiceFieldResponse[]
): any | undefined => {
	const currentFieldConfig = fieldsList.find((item) => item.name === data.name);
	if (currentFieldConfig) {
		const payload = {
			name: currentFieldConfig.name,
			placeholder: currentFieldConfig.placeHolder || '',
			label: currentFieldConfig.title || '',
			helperText: currentFieldConfig.errorText || currentFieldConfig.hint || getServiceHelperText(currentFieldConfig),
			error: !!currentFieldConfig.errorText,
			inputProps: {
				inputMode: currentFieldConfig.type === 'NUM' ? 'decimal' : 'text',
				maxLength: currentFieldConfig.maxLength ?? undefined,
			},
			value: data.value,
			min: currentFieldConfig.minAmount ?? undefined,
			max: currentFieldConfig.maxAmount ?? undefined,
		};

		return payload;
	}
};

export const checkIsAmountServiceFieldName = (name: string): boolean => name === PORTMONE_AMOUNT_FIELD_NAME;

export const checkIsAmountServiceField = (
	item:
		| IAddServiceFieldValueRequest
		| IUpdateServiceFieldValueRequest
		| IServiceFieldValueResponse
		| IServiceFieldResponse
): boolean => checkIsAmountServiceFieldName(item.name);

export const checkIsServiceValueAllowed = (
	value: string,
	config: IServiceFieldResponse | null,
	isZeroAllowed = false
): boolean => {
	let isValid = true;

	if (!isZeroAllowed && config?.minAmount && +value < config.minAmount) {
		isValid = false;
	}
	if (config?.maxAmount && +value > config.maxAmount) {
		isValid = false;
	}
	if (config?.maxLength && value.length > config.maxLength) {
		isValid = false;
	}
	return isValid;
};

export const getServiceHelperText = (config: IServiceFieldResponse | undefined): string => {
	let text = '';
	if ((config && config?.minAmount) || config?.maxAmount) {
		text = `${config?.minAmount ? `Від ${config.minAmount}${CURRENCY_LABEL.UAH}` : ''} ${
			config?.maxAmount ? `до ${config.maxAmount}${CURRENCY_LABEL.UAH}` : ''
		}`;
	}
	return text;
};

export const getTariffItemDataPreviewByKey = (data: ITariffItemData, key: string): ITariffItemData => ({
	name: data[`${key}_name`] as string,
	state: data[`${key}_state`],
	value: data[key],
	payee: data.payee,
});

export const sortServicesFieldsByFieldsNames = (list: IServiceFieldResponse[]): IServiceFieldResponse[] =>
	list.sort((a, b) => {
		if (a.name === PORTMONE_AMOUNT_FIELD_NAME || b.name === PORTMONE_AMOUNT_FIELD_NAME) {
			return a.name === PORTMONE_AMOUNT_FIELD_NAME ? 1 : -1;
		}
		return a.name.localeCompare(b.name);
	});

export const getContractNumberFieldName = (data: IGetServiceDataResponse | IReceiptOtherServiceResponse): string =>
	data.dataObject?.contractNumber.split('.').slice(-1)[0] ?? '';

export const findServiceProviderAccountIdValue = (
	fieldValues: IAddServiceFieldValueRequest[] | IUpdateServiceFieldValueRequest[] | IServiceFieldValueResponse[],
	data: IGetServiceDataResponse | IReceiptOtherServiceResponse
): string => {
	const providerAccountIdFieldName = getContractNumberFieldName(data);
	return fieldValues.find((item) => item.name === providerAccountIdFieldName)?.value ?? '';
};

export const findPortmoneProviderAmountFieldValue = (serviceRequestData: IOtherServicePaymentRequest): string => {
	const serviceFieldsDataList = Object.values(serviceRequestData?.serviceProviderData[0]?.serviceFieldsData);
	if (serviceFieldsDataList.length) {
		const key = Object.keys(serviceRequestData.serviceProviderData[0].serviceFieldsData).find(
			checkIsAmountServiceFieldName
		);
		if (key) {
			return serviceRequestData.serviceProviderData[0].serviceFieldsData[key].toString();
		}
	}
	return '';
};

export const flatServiceBillRequestData = (
	billRequestData: (IBillCreateRequestArrayDataItem | IServiceBillError)[]
): {
	serviceInfo: IBillCreateRequestArrayDataItem['services'];
	tariffItem: IBillCreateRequestArrayDataItem['tariffItem'];
}[] => {
	return billRequestData.reduce(
		(
			acc: {
				serviceInfo: IBillCreateRequestArrayDataItem['services'];
				tariffItem: IBillCreateRequestArrayDataItem['tariffItem'];
			}[],
			curr
		) => {
			if (Array.isArray(curr)) {
				return [...acc, ...flatServiceBillRequestData(curr)];
			}

			// Skips items with error
			if ('error' in curr) {
				return acc;
			}

			return [...acc, { serviceInfo: { ...curr.services }, tariffItem: curr.tariffItem }];
		},
		[]
	);
};

export const flatReceiptServiceBillRequestData = (
	billRequestData: (IBillRequestData | IServiceBillError)[]
): IBillRequestData[] => {
	return billRequestData.reduce((acc, curr) => {
		if (Array.isArray(curr)) {
			return [...acc, ...flatReceiptServiceBillRequestData(curr)];
		}

		// Skips items with error
		if ('error' in curr) {
			return acc;
		}

		return [...acc, curr];
	}, [] as IBillRequestData[]);
};

export const getErrorMessageFromTarrifItems = (tarrifs?: IServiceBillRequestData | IBillRequestData): string => {
	if (!tarrifs) return '';
	if (!Array.isArray(tarrifs)) {
		if ('error' in tarrifs) return tarrifs.error as string;
		return '';
	}
	if (tarrifs.every((item) => !Array.isArray(item) && 'error' in item)) {
		const [first] = tarrifs as IServiceBillError[];

		if (Array.isArray(first)) return '';

		return first.error as string;
	}
	return '';
};

export const getServiceTariffItemsList = (data: {
	// key as id
	[key: string]: ITariffItemData;
}): ITariffItemData[] => {
	return Object.values(data)
		.map((item) => {
			// remove all items if it readings of a meter
			const objectKeysWithoutCounters = Object.keys(item)
				.reduce(
					(keysAcc, key) => (key.includes('_name') ? [...keysAcc, key.replace('_name', '')] : keysAcc),
					[] as string[]
				)
				.filter((item) => item !== 'counter');
			return objectKeysWithoutCounters.reduce(
				(objectAcc, key) => ({
					...objectAcc,
					name: item.name,
					[key]: item[key],
					[`${key}_name`]: item[`${key}_name`],
					[`${key}_state`]: item[`${key}_state`],
				}),
				{} as ITariffItemData
			);
		})
		.filter((item) => Object.keys(item).length);
};

export const getServiceBillRequestFieldsNames = (data: IServiceDataObject): string[] =>
	Object.values(data).reduce(
		(acc, item) =>
			typeof item === 'string' && item.includes('@bill.payerData.') ? [...acc, item.split('.').slice(-1)[0]] : acc,
		[] as string[]
	);

export const getServicesFieldsList = (
	fieldsList: IServiceFieldResponse[],
	serviceDataObject: IServiceDataObject,
	isOnlyRequired?: boolean
): IServiceFieldResponse[] => {
	const providerFieldsNameList: string[] = getServiceBillRequestFieldsNames(serviceDataObject);
	const serviceFieldsList = fieldsList.filter((item) =>
		isOnlyRequired
			? providerFieldsNameList.includes(item.name) && item.required === CHECK_RESPONSE.YES
			: providerFieldsNameList.includes(item.name)
	);
	return serviceFieldsList;
};

export const getServiceBillRequestKeyCombination = (
	data: IServiceDataObject
): { reqKeyName: string; fieldValueName: string }[] => {
	const fieldValueNamePattern = '@bill.payerData.';
	return Object.entries(data).reduce((acc, [reqKeyName, value]) => {
		if (typeof value !== 'string' || !value.includes(fieldValueNamePattern)) return acc;

		return [...acc, { reqKeyName, fieldValueName: value.split(fieldValueNamePattern)[1] }];
	}, [] as { reqKeyName: string; fieldValueName: string }[]);
};

export const getCreateBillRequestServiceFieldsData = (
	data: IServiceDataObject,
	fieldsValuesList: IAddServiceFieldValueRequest[] | IUpdateServiceFieldValueRequest[]
): ICreateBillRequestServiceFieldsData => {
	const serviceFieldsDataKeyCombination = getServiceBillRequestKeyCombination(data);
	const serviceFieldsData = serviceFieldsDataKeyCombination.reduce((acc, item) => {
		const fieldValue = fieldsValuesList.find((fieldValues) => fieldValues.name === item.fieldValueName)?.value ?? '';

		return fieldValue
			? {
					...acc,
					[item.reqKeyName]: fieldValue,
			  }
			: acc;
	}, {} as ICreateBillRequestServiceFieldsData);
	return serviceFieldsData;
};

const getLegacyServiceIncomeBalance = (service: IBillCreateRequestArrayDataItem): string | null => {
	const amount = service.tariffItem?.amount;

	return amount === undefined || amount === null || amount === '' ? null : convertUAHToCoins(amount).toString();
};

export const normalizeServiceBillRequestData = (
	data: IServiceBillRequestData | null | undefined
): INormalizedServiceBillRequestItem[] => {
	if (!data) return [];

	const responseItems = Array.isArray(data) ? data : [data];

	return responseItems.reduce((result, item) => {
		if ('error' in item) return result;

		const providerType = item.providerType ?? item.services.providerType;
		const incomeBalance = item.incomeBalance === undefined ? getLegacyServiceIncomeBalance(item) : item.incomeBalance;
		const recipientName = item.recipientName ?? item.tariffItem.payee?._ ?? item.regDataObject.name;
		const providerService = item.providerService ?? item.services.name;
		const displayName =
			providerType === PROVIDER_TYPE.PORTMONE && incomeBalance !== null
				? item.tariffItem.payee?._ ?? providerService
				: providerService;
		const code = item.code ?? item.services.code;
		const amountToPay =
			item.amountToPay === undefined && incomeBalance !== null ? Number(incomeBalance) : item.amountToPay ?? null;
		const providerAccountId = item.providerAccountId ?? item.tariffItem.contractNumber?._ ?? '';
		const billId = item.billId ?? item.tariffItem.id;
		const shortId = item.shortId ?? item.tariffItem.shortId;
		const reservedBillId = item.reservedBillId ?? item.tariffItem.reservedBillId;
		const selectionKey = [providerType, code, item.services.id, providerService, providerAccountId].join(':');

		return [
			...result,
			{
				...item,
				selectionKey,
				recipientName,
				providerService,
				displayName,
				code,
				providerType,
				incomeBalance,
				amountToPay,
				fee: item.fee ?? '0',
				valuesDisabled: item.valuesDisabled ?? false,
				providerAccountId,
				billId,
				serviceFieldsData: item.serviceFieldsData ?? {},
				requestedService: item.requestedService ?? false,
				...(shortId !== undefined ? { shortId } : {}),
				...(reservedBillId ? { reservedBillId } : {}),
			},
		];
	}, [] as INormalizedServiceBillRequestItem[]);
};

export const groupServiceBillRequestItemsByRecipient = (
	services: INormalizedServiceBillRequestItem[]
): IServiceBillRequestGroup[] => {
	return services.reduce((groups, service) => {
		const existingGroup = groups.find((group) => group.recipientName === service.recipientName);

		if (existingGroup) {
			existingGroup.services.push(service);
			return groups;
		}

		return [...groups, { recipientName: service.recipientName, services: [service] }];
	}, [] as IServiceBillRequestGroup[]);
};

export const getServicePaymentRecipientCount = (services: INormalizedServiceBillRequestItem[]): number => {
	return new Set(
		services
			.filter((service) => Number(service.incomeBalance) > 0)
			.map((service) => service.regDataObject.account?.trim())
			.filter(Boolean)
	).size;
};

const normalizeServiceInstructionTitle = (value: string): string =>
	value
		.toLocaleLowerCase('uk-UA')
		.replace(/["'«»„“”]/g, '')
		.replace(/\s+/g, ' ')
		.trim();

const isServiceNameIncludedInRecipient = (recipientName: string, serviceName: string): boolean => {
	const normalizedRecipientName = normalizeServiceInstructionTitle(recipientName);
	const normalizedServiceName = normalizeServiceInstructionTitle(serviceName);

	return (
		!!normalizedServiceName &&
		(normalizedRecipientName === normalizedServiceName || normalizedRecipientName.endsWith(` ${normalizedServiceName}`))
	);
};

export const getServicePaymentInstructionServiceName = (service: INormalizedServiceBillRequestItem): string => {
	const serviceName = (service.regDataObject.serviceTitle || service.displayName).trim();
	if (!serviceName || isServiceNameIncludedInRecipient(service.recipientName, serviceName)) return '';

	const openingParenthesisIndex = serviceName.indexOf('(');
	const closingParenthesisIndex = serviceName.lastIndexOf(')');
	if (openingParenthesisIndex > 0 && closingParenthesisIndex > openingParenthesisIndex) {
		const serviceNamePrefix = serviceName.slice(0, openingParenthesisIndex).trim();
		if (isServiceNameIncludedInRecipient(service.recipientName, serviceNamePrefix)) {
			return serviceName
				.slice(openingParenthesisIndex + 1, closingParenthesisIndex)
				.trim()
				.replace(/[.,;:!?]+$/, '');
		}
	}

	return serviceName;
};

export const getServicePaymentInstructionNumber = (services: INormalizedServiceBillRequestItem[]): string =>
	services
		.map((service) => service.reservedBillId ?? service.tariffItem.reservedBillId)
		.find((instructionNumber): instructionNumber is string => Boolean(instructionNumber)) ?? '';

export const groupServicePaymentInstructionItemsByRecipient = (
	services: INormalizedServiceBillRequestItem[]
): IServicePaymentInstructionGroup[] => {
	return services
		.filter((service) => Number(service.incomeBalance) > 0)
		.reduce((groups, service) => {
			const recipientName = service.recipientName.trim();
			const normalizedRecipientName = normalizeServiceInstructionTitle(recipientName);
			const key = normalizedRecipientName
				? `${service.providerType}:${normalizedRecipientName}`
				: service.selectionKey;
			const existingGroup = groups.find((group) => group.key === key);

			if (existingGroup) {
				existingGroup.services.push(service);
				return groups;
			}

			return [...groups, { key, recipientName, services: [service] }];
		}, [] as IServicePaymentInstructionGroup[]);
};

export const isOtherPaymentAmountAllowed = (amount: string | number): boolean =>
	validateAmountLimits(amount, OTHER_PAYMENT_MAX_AMOUNT, OTHER_PAYMENT_MIN_AMOUNT);

export const getMultiServicePaymentProviderData = (
	services: INormalizedServiceBillRequestItem[],
	feeList: ServiceFeeRecord | null,
	additionalServiceFieldsData: IOtherServiceProviderServiceFieldsData = {}
): IOtherServiceProviderRequest[] => {
	return services
		.filter((service) => Number(service.incomeBalance) > 0)
		.map((service) => {
			const legacyServiceFieldsData = getServiceBillRequestKeyCombination(service.dataObject).reduce(
				(fieldsData, { reqKeyName, fieldValueName }) => {
					const value = (service.tariffItem[reqKeyName] as { _: string } | undefined)?._;

					return value ? { ...fieldsData, [fieldValueName]: value } : fieldsData;
				},
				{} as IOtherServiceProviderServiceFieldsData
			);
			const shortId = service.shortId ?? service.tariffItem.shortId;
			const reservedBillId = service.reservedBillId ?? service.tariffItem.reservedBillId;

			return {
				code: service.code,
				serviceId: service.services.id,
				incomeBalance: service.incomeBalance as string,
				fee: (feeList?.[service.selectionKey]?.fee ?? service.fee).toString(),
				providerType: service.providerType,
				providerService: service.providerService,
				providerAccountId: service.providerAccountId,
				billId: service.billId,
				payed: 0,
				serviceFieldsData: {
					...legacyServiceFieldsData,
					...service.serviceFieldsData,
					...(shortId !== undefined ? { shortId } : {}),
					...(reservedBillId ? { reservedBillId } : {}),
					...additionalServiceFieldsData,
				},
			};
		});
};

export const filterEmptyAmountBillCreateServices = (services: IBillCreateRequestArrayDataItem[]) =>
	services.filter((service) => Number(service.tariffItem.amount) > 0);
